import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { blogAPI } from '../services/api';

const BlogCreate = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedTagIds, setSelectedTagIds] = useState([]);

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    author_name: '',
    category: '',
    tags: [],
    cover_image_url: '',
    is_published: false,
  });

  // Load available tags
  useEffect(() => {
    const fetchTags = async () => {
      try {
        const response = await blogAPI.getAllTags();
        setAvailableTags(response.data.tags || []);
      } catch (err) {
        console.error('Error fetching tags:', err);
      }
    };
    fetchTags();
  }, []);

  // Generate slug from title
  useEffect(() => {
    if (formData.title) {
      const slug = formData.title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
      setFormData(prev => ({ ...prev, slug }));
    }
  }, [formData.title]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleTagsChange = (e) => {
    const tags = e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag);
    setFormData((prev) => ({
      ...prev,
      tags,
    }));
  };

  const handleCreatePost = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const postData = {
        ...formData,
        tags: formData.tags.length > 0 ? formData.tags : [],
      };

      const response = await blogAPI.createPost(postData);

      if (response.data.success || response.data.post) {
        const postId = response.data.post.id;

        // Add tags to post if any selected
        if (selectedTagIds.length > 0) {
          try {
            await blogAPI.addTagsToPost(postId, selectedTagIds);
          } catch (tagError) {
            console.error('Error adding tags:', tagError);
          }
        }

        setSuccess('Post created successfully!');
        setFormData({
          title: '',
          slug: '',
          excerpt: '',
          content: '',
          author_name: '',
          category: '',
          tags: [],
          cover_image_url: '',
          is_published: false,
        });
        setSelectedTagIds([]);

        // Auto-clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Failed to create post');
      }
    } catch (err) {
      console.error('Failed to create post:', err);
      setError('Failed to create post');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header title="Create Blog Post" />
        <div className="flex-1 overflow-y-auto p-6">
          {/* Success/Error Messages */}
          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-6">
              <div className="flex items-center">
                <span className="text-green-500 mr-2">✅</span>
                <span>{success}</span>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
              <div className="flex items-center">
                <span className="text-red-500 mr-2">❌</span>
                <span>{error}</span>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="mb-6">
            <div className="flex items-center space-x-2 text-sm text-gray-500">
              <a href="/blog" className="hover:text-blue-600 transition">Blog Management</a>
              <span>→</span>
              <span className="text-gray-700 font-medium">Create New Post</span>
            </div>
          </div>

          {/* Form */}
          <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-xl font-bold text-gray-900 mb-8">Blog Post Details</h2>

            <form onSubmit={handleCreatePost} className="space-y-6">
              {/* Basic Information */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Basic Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Post Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                      placeholder="Enter post title"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Slug <span className="text-red-500">*</span>
                    </label>
                    <div className="flex items-center">
                      <input
                        type="text"
                        name="slug"
                        value={formData.slug}
                        onChange={handleInputChange}
                        className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                        placeholder="post-slug"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const slug = formData.title
                            .toLowerCase()
                            .replace(/[^a-z0-9]+/g, '-')
                            .replace(/(^-|-$)/g, '');
                          setFormData(prev => ({ ...prev, slug }));
                        }}
                        className="ml-3 px-4 py-3 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition text-sm font-medium"
                      >
                        Generate
                      </button>
                    </div>
                    <p className="mt-2 text-xs text-gray-500">
                      The slug will be used in the URL: <span className="font-mono">yourdomain.com/blog/{formData.slug}</span>
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Author Name
                    </label>
                    <input
                      type="text"
                      name="author_name"
                      value={formData.author_name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                      placeholder="Author name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                      required
                    >
                      <option value="">Select a category</option>
                      <option value="Technology">Technology</option>
                      <option value="AI & Machine Learning">AI & Machine Learning</option>
                      <option value="Marketing">Marketing</option>
                      <option value="Business">Business</option>
                      <option value="Design">Design</option>
                      <option value="Development">Development</option>
                      <option value="Data Science">Data Science</option>
                      <option value="Product">Product</option>
                      <option value="Industry News">Industry News</option>
                      <option value="Tutorials">Tutorials</option>
                      <option value="Case Studies">Case Studies</option>
                      <option value="Best Practices">Best Practices</option>
                    </select>
                    <p className="mt-2 text-xs text-gray-500">
                      Choose the most relevant category for your post
                    </p>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Article Tags
                    </label>
                    <div className="flex flex-wrap gap-2 p-4 bg-white border border-gray-300 rounded-lg min-h-[100px]">
                      {availableTags.map((tag) => (
                        <button
                          key={tag.id}
                          type="button"
                          onClick={() => {
                            setSelectedTagIds(prev =>
                              prev.includes(tag.id)
                                ? prev.filter(id => id !== tag.id)
                                : [...prev, tag.id]
                            );
                          }}
                          className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                            selectedTagIds.includes(tag.id)
                              ? 'text-white shadow-md'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                          style={{
                            backgroundColor: selectedTagIds.includes(tag.id) ? tag.color : undefined
                          }}
                        >
                          {tag.name}
                        </button>
                      ))}
                      {availableTags.length === 0 && (
                        <p className="text-gray-400 text-sm">No tags available. Please create tags first.</p>
                      )}
                    </div>
                    <p className="mt-2 text-xs text-gray-500">
                      Click to select/deselect tags for this article
                    </p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Content</h3>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cover Image URL
                  </label>
                  <input
                    type="text"
                    name="cover_image_url"
                    value={formData.cover_image_url}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    placeholder="https://example.com/cover.jpg"
                  />
                  <p className="mt-2 text-xs text-gray-500">
                    URL of the cover image for the blog post
                  </p>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Excerpt
                  </label>
                  <textarea
                    name="excerpt"
                    value={formData.excerpt}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    placeholder="Brief summary of the blog post..."
                  />
                  <p className="mt-2 text-xs text-gray-500">
                    A short preview of the post content (displayed on blog listing)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Content <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="content"
                    value={formData.content}
                    onChange={handleInputChange}
                    rows={15}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition font-mono text-sm"
                    placeholder="# Blog Post Content\n\nWrite your blog post here using markdown...\n\n## Section 1\n\nParagraph text...\n\n![Image](https://example.com/image.jpg)\n\n## Section 2\n\nList:\n- Item 1\n- Item 2\n- Item 3"
                    required
                  />
                  <p className="mt-2 text-xs text-gray-500">
                    Write your content in markdown format. Supports headings, images, lists, links, etc.
                  </p>
                </div>
              </div>

              {/* Publishing */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Publishing</h3>

                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="is_published"
                    name="is_published"
                    checked={formData.is_published}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500 border-gray-300"
                  />
                  <label htmlFor="is_published" className="text-sm font-medium text-gray-700">
                    Publish immediately after creating
                  </label>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3">
                <a
                  href="/blog"
                  className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium"
                >
                  Cancel
                </a>
                <button
                  type="submit"
                  disabled={loading}
                  className={`px-6 py-3 rounded-lg font-medium transition ${
                    loading
                      ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {loading ? (
                    <span className="flex items-center">
                      <span className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" />
                      Creating...
                    </span>
                  ) : (
                    'Create Post'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogCreate;
